import React, { useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { FaFileUpload, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import "./CmsImageField.css";

const isBlobUrl = (url) => typeof url === "string" && url.startsWith("blob:");
const MAX_CATALOGUE_FILE_BYTES = 100 * 1024 * 1024;

/**
 * Deferred file picker for catalogue PDFs — uploads on parent Save only.
 */
const CmsFileField = ({
  label,
  value,
  onChange,
  onPendingFile,
  accept = ".pdf,application/pdf",
  helpText = "PDF/Word up to 100MB. File is stored only after you click Save.",
  maxBytes = MAX_CATALOGUE_FILE_BYTES,
}) => {
  const inputRef = useRef(null);
  const [selecting, setSelecting] = useState(false);

  const displayName = (() => {
    if (!value) return "";
    if (isBlobUrl(value)) return "Selected file (pending save)";
    try {
      const parts = value.split("/");
      return parts[parts.length - 1] || value;
    } catch {
      return value;
    }
  })();

  const handleFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    const file = files[0];
    if (file.size > maxBytes) {
      const maxMb = Math.round(maxBytes / (1024 * 1024));
      toast.error(`File is too large. Maximum size is ${maxMb}MB.`);
      return;
    }

    setSelecting(true);
    try {
      if (isBlobUrl(value)) URL.revokeObjectURL(value);
      const blobUrl = URL.createObjectURL(file);
      onPendingFile?.(file);
      onChange(blobUrl);
      toast.success("File selected — click Save to keep it");
    } finally {
      setSelecting(false);
    }
  };

  const clear = () => {
    if (isBlobUrl(value)) URL.revokeObjectURL(value);
    onPendingFile?.(null);
    onChange("");
    toast.info("File removed — click Save to apply");
  };

  return (
    <div className="cms-image-field">
      {label && <label className="form-label">{label}</label>}
      <div className="cms-image-row">
        <div className="cms-image-preview cms-file-preview">
          {displayName ? <span title={displayName}>{displayName}</span> : <span>No file</span>}
        </div>
        <div className="cms-image-actions">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            hidden
            onChange={handleFiles}
          />
          <Button
            type="button"
            variant="outline-dark"
            size="sm"
            disabled={selecting}
            onClick={() => inputRef.current?.click()}
          >
            {selecting ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                <FaFileUpload /> Choose file
              </>
            )}
          </Button>
          {value && (
            <Button type="button" variant="outline-danger" size="sm" onClick={clear}>
              <FaTimes /> Remove
            </Button>
          )}
        </div>
      </div>
      {helpText && <div className="form-text">{helpText}</div>}
    </div>
  );
};

export default CmsFileField;
